import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { BusinessUnit } from '@/constants/businessUnits';
import { toast } from 'sonner';

export interface CostPositionAllocation {
  id: string;
  category_id: string;
  business_unit: BusinessUnit;
  percentage: number;
}

export const useCostPositionAllocations = () => {
  return useQuery({
    queryKey: ['cost-position-bu-allocations'],
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .from('cost_position_bu_allocations')
        .select('*')
        .limit(10000);
      if (error) throw error;
      return (data || []) as CostPositionAllocation[];
    },
    staleTime: 60_000,
  });
};

export const useAllocationsByCategory = () => {
  const { data, ...rest } = useCostPositionAllocations();
  const map = new Map<string, CostPositionAllocation[]>();
  (data || []).forEach((a) => {
    const arr = map.get(a.category_id) || [];
    arr.push(a);
    map.set(a.category_id, arr);
  });
  return { allocationsByCategory: map, ...rest };
};

export const useSaveCostPositionAllocations = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      category_id,
      allocations,
    }: {
      category_id: string;
      allocations: { business_unit: BusinessUnit; percentage: number }[];
    }) => {
      const { error } = await (supabase as any).rpc('set_cost_position_allocations', {
        p_category_id: category_id,
        p_allocations: allocations,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['cost-position-bu-allocations'] });
      qc.invalidateQueries({ queryKey: ['expense-categories'] });
      qc.invalidateQueries({ queryKey: ['bu-pnl'] });
    },
    onError: (e: Error) => toast.error(e.message || 'Failed to save allocations'),
  });
};
